import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { oapi } from '@/lib/officialsApi';
import { SettingsCard } from './SettingsCard';

const PERMS = [['view', 'View payments'], ['rates', 'Manage rates & settings'], ['verify', 'Verify games'], ['approve', 'Approve payments'],
  ['record', 'Record payments'], ['export', 'Export reports']];

const SchedulerPerms = ({ email, list, onToggle }) => (
  <div className="rounded-xl border px-3 py-2.5" data-testid={`pay-perms-${email}`}>
    <div className="text-sm font-semibold text-slate-800 truncate mb-2">{email}</div>
    <div className="grid grid-cols-2 gap-x-3 gap-y-2">
      {PERMS.map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-xs text-slate-600">
          <Switch checked={list.includes(k)} onCheckedChange={v => onToggle(email, k, v)} data-testid={`pay-perm-${email}-${k}`} />{l}
        </label>
      ))}
    </div>
  </div>
);

// Owner-only: per-scheduler payment permissions (all off by default).
export const PaymentPermsCard = ({ orgId, schedulers }) => {
  const [perms, setPerms] = useState(null);
  const [busy, setBusy] = useState(false);
  const emails = schedulers.map(s => s.email.toLowerCase());
  useEffect(() => {
    oapi('/officials/payments/settings', { orgId }).then(d => setPerms(d.payments.permissions || {})).catch(e => toast.error(e.message));
  }, [orgId, schedulers]);
  const toggle = (email, k, on) => setPerms(p => {
    let cur = new Set(p[email] || []);
    if (on) { cur.add(k); cur.add('view'); } else if (k === 'view') cur = new Set(); else cur.delete(k);
    return { ...p, [email]: [...cur] };
  });
  const save = async () => {
    setBusy(true);
    try {
      const r = await oapi('/officials/payments/settings', { method: 'PUT', orgId, body: { permissions: Object.fromEntries(emails.map(e => [e, perms[e] || []])) } });
      setPerms(r.payments.permissions || {});
      toast.success('Payment permissions saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <SettingsCard title="Payment permissions" desc="Schedulers can't see any money until you turn it on. You always have full access." testId="settings-pay-perms">
      {!perms ? <Loader2 className="w-5 h-5 animate-spin text-slate-400" /> : (
        <>
          <div className="space-y-2">{emails.map(e => <SchedulerPerms key={e} email={e} list={perms[e] || []} onToggle={toggle} />)}</div>
          {!emails.length && <p className="text-sm text-slate-400" data-testid="pay-perms-empty">Add a scheduler above to give them payment access.</p>}
          {emails.length > 0 && (
            <div className="flex justify-end mt-3"><Button size="sm" disabled={busy} onClick={save} className="bg-[#00247D] hover:bg-[#001A5C]" data-testid="pay-perms-save">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Save permissions</Button></div>
          )}
        </>
      )}
    </SettingsCard>
  );
};
